import { useEffect, useState } from "react";
import { X, Ban } from "lucide-react";

/**
 * Modal de confirmação para encerrar o ciclo de um visitante, com campo de observação.
 * Substitui o window.confirm() padrão por uma UX consistente com o resto do app.
 *
 * Props:
 *  - open: boolean
 *  - onClose: () => void
 *  - onConfirm: (obs: string) => Promise<void> | void   -- callback ao confirmar
 *  - visitante: { nome, status_atual, ... }
 */
export default function FinalizarCicloModal({ open, onClose, onConfirm, visitante }) {
  const [obs, setObs] = useState("");
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (open) {
      setObs("");
      setSubmitting(false);
    }
  }, [open]);

  if (!open || !visitante) return null;

  const dentro = visitante.status_atual === "DENTRO";

  const handleConfirm = async () => {
    setSubmitting(true);
    try {
      await onConfirm(obs.trim());
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div
      data-testid="finalizar-ciclo-modal"
      className="fixed inset-0 z-[110] flex items-center justify-center bg-black/70 backdrop-blur-sm p-4"
      onClick={() => !submitting && onClose()}
    >
      <div
        className="bg-brand-deep border border-amber-400/30 rounded-xl shadow-2xl w-full max-w-md"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-center justify-between px-5 py-3 border-b border-brand-ice/15">
          <div className="flex items-center gap-3">
            <div className="p-2 rounded-lg bg-amber-500/20 text-amber-300">
              <Ban className="w-4 h-4" />
            </div>
            <div>
              <div className="text-xs uppercase tracking-wider text-brand-ice/60">Encerrar Ciclo</div>
              <div className="font-heading font-bold text-brand-ice text-sm">
                {visitante.nome}
              </div>
            </div>
          </div>
          <button
            data-testid="close-finalizar-ciclo"
            onClick={onClose}
            disabled={submitting}
            className="p-2 rounded-lg text-brand-ice/60 hover:text-brand-ice hover:bg-white/10 transition-colors disabled:opacity-50"
            aria-label="Fechar"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Body */}
        <div className="p-5 space-y-4">
          <div className="text-sm text-brand-ice/80 space-y-2">
            <p>Ao encerrar o ciclo deste visitante:</p>
            <ul className="text-xs text-brand-ice/70 space-y-1 list-disc list-inside pl-1">
              <li>Novos movimentos ficam <strong>bloqueados</strong> (mesmo dentro da validade).</li>
              {dentro && (
                <li>Como o visitante está <strong className="text-emerald-300">DENTRO</strong>, será registrada uma <strong>saída automática</strong>.</li>
              )}
              <li>Só será possível reabrir o acesso através de um <strong>novo cadastro</strong>.</li>
            </ul>
          </div>

          <div className="space-y-1.5">
            <label className="text-[10px] uppercase tracking-wider font-heading font-bold text-brand-ice/70" htmlFor="finalizar-ciclo-obs">
              Motivo / Observação (opcional)
            </label>
            <textarea
              id="finalizar-ciclo-obs"
              data-testid="finalizar-ciclo-obs"
              value={obs}
              onChange={(e) => setObs(e.target.value.slice(0, 240))}
              placeholder="Ex.: Hóspede fez checkout antes da data, Visitante não retornou, etc."
              rows={3}
              maxLength={240}
              className="w-full bg-white/10 border border-brand-ice/20 rounded-lg px-3 py-2 text-sm text-brand-ice placeholder:text-brand-ice/40 focus:outline-none focus:border-amber-400/50 resize-none"
            />
            <div className="text-[10px] text-brand-ice/40 text-right">{obs.length}/240</div>
          </div>
        </div>

        {/* Footer */}
        <div className="px-5 py-3 border-t border-brand-ice/15 flex justify-end gap-2">
          <button
            type="button"
            onClick={onClose}
            disabled={submitting}
            className="px-4 py-2 rounded-lg bg-white/10 hover:bg-white/20 text-brand-ice font-heading font-bold text-sm transition-colors disabled:opacity-50"
          >
            Cancelar
          </button>
          <button
            type="button"
            data-testid="confirmar-finalizar-ciclo"
            onClick={handleConfirm}
            disabled={submitting}
            className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-amber-500 hover:bg-amber-600 text-white font-heading font-bold text-sm transition-colors disabled:opacity-60"
          >
            <Ban className="w-4 h-4" />
            {submitting ? "Encerrando..." : "Encerrar Ciclo"}
          </button>
        </div>
      </div>
    </div>
  );
}
